import { createContext, useContext } from 'react';

import { InboxEntryViewFields, WebSocketEntityType } from '@this+that/models';

// Connection states
export enum ConnectionState {
  DISCONNECTED = 'disconnected',
  CONNECTING = 'connecting',
  CONNECTED = 'connected',
  RECONNECTING = 'reconnecting',
}

// Event types for entity updates
export enum WebSocketEventType {
  INSERT = 'INSERT',
  MODIFY = 'MODIFY',
  REMOVE = 'REMOVE',
  REMOVE_BATCH = 'REMOVE_BATCH',
  RECONNECT_AFTER_LONG_PAUSE = 'RECONNECT_AFTER_LONG_PAUSE',
}

// What a message says about its item beyond the item itself.
export interface WebSocketEventMeta {
  reopened?: boolean;
  previous?: InboxEntryViewFields;
  alreadyDeleted?: boolean;
}

export type WebSocketEventCallback<T = unknown> = (
  data: {
    eventType: WebSocketEventType;
    entityType: WebSocketEntityType;
    item?: T;
  } & WebSocketEventMeta,
) => void;

// Context interface
export interface WebSocketContextValue {
  // Connection control
  reconnect: () => void;
  disconnect: () => void;

  // Connection state
  connectionState: ConnectionState;
  isConnected: boolean;

  // Event subscription
  subscribe: <T = unknown>(
    entityType: WebSocketEntityType,
    eventType: WebSocketEventType | WebSocketEventType[],
    callback: WebSocketEventCallback<T>,
  ) => () => void;
}

// Create context with undefined default (will throw if used outside provider)
export const WebSocketContext = createContext<
  WebSocketContextValue | undefined
>(undefined);

// Custom hook to use WebSocket context
export function useWebSocket(): WebSocketContextValue {
  const context = useContext(WebSocketContext);

  if (!context) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }

  return context;
}
